import React, { useEffect, useState } from "react";
import API from "../../utils/API";
import { FEATURES } from "../../config/features";
import { ownSplitFor } from "../../data/BookDataProvider";
import {
  COMMISSION_RATE,
  daysUntil,
  formatDate,
  formatMoney,
  renewalInfo
} from "../../data/format";
import { Badge, Card, EmptyState, BookIcon, FileIcon } from "../../ui";
import { LobBadge, OpenCasesCard } from "../shared";

/**
 * The referral-tier household Overview (E §5) — the D2 policy row,
 * "complete, not redacted": exactly the permitted fields (line, carrier,
 * policy number, status, dates, premium, THEIR split), composed to read
 * as a finished object. No chevrons, no "view full policy", no document
 * icons — those affordances don't exist at this tier rather than being
 * disabled. Rows are informational, not clickable.
 */

function policyStatus(policy, days) {
  if (policy.policyActive === false) {
    return { label: "Lapsed", variant: "neutral" };
  }
  if (days != null && days <= 14) {
    // E QA L-2: "Renews soon" (amber) inside 14 days only, not 60.
    return { label: "Renews soon", variant: "warning" };
  }
  return { label: "Active", variant: "success", dot: true };
}

function D2PolicyRow({ policy, identity }) {
  const renewal = renewalInfo(policy);
  const days = renewal ? daysUntil(renewal.date) : null;
  const status = policyStatus(policy, days);
  const split = ownSplitFor(policy, identity);
  const share =
    split == null ? null : (Number(policy.premium) || 0) * COMMISSION_RATE * (split / 100);

  return (
    <div className="feat-d2row">
      <div className="feat-d2row__top">
        <LobBadge lobKey={policy.lobKey} lob={policy.lob} />
        <span className="feat-d2row__carrier">{policy.carrier || "Unknown carrier"}</span>
        {policy.policyNumber ? (
          <span className="feat-cell-secondary">#{policy.policyNumber}</span>
        ) : null}
        <span style={{ flex: 1 }} />
        <Badge variant={status.variant} dot={status.dot}>
          {status.label}
        </Badge>
      </div>
      <div className="feat-d2row__dates">
        Effective {formatDate(policy.effectiveDate)}
        {renewal ? (
          <>
            {" "}
            → renews {formatDate(renewal.date)}
            {renewal.estimated ? " (est.)" : ""}
          </>
        ) : null}
      </div>
      <div className="feat-d2row__money">
        <span className="feat-cell-primary">{formatMoney(policy.premium)}/yr</span>
        {split != null ? (
          <span className="feat-d2row__share">
            Your share · {split}% ≈ {formatMoney(share)}/yr
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** R16/§3c: their OWN intake uploads — labeled as theirs, implying nothing more. */
function OwnUploadsCard({ household }) {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    if (!FEATURES.documentsByOwner) {
      return undefined;
    }
    let cancelled = false;
    API.getDocumentsByOwner("Client", household.id)
      .then((res) => {
        if (!cancelled) {
          setDocs(Array.isArray(res.data) ? res.data : []);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [household]);

  if (!FEATURES.documentsByOwner || docs.length === 0) {
    return null; // absent, not empty
  }
  return (
    <Card title="Your uploads">
      <div className="feat-feed">
        {docs.map((doc) => (
          <div key={doc._id} className="feat-feed__row" style={{ cursor: "default" }}>
            <FileIcon size={14} />
            <span className="feat-feed__main">
              <span className="feat-feed__title">{doc.docName || "Untitled document"}</span>
              <span className="feat-feed__meta">{formatDate(doc.created_at)}</span>
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ReferralOverview({ household, identity, openCases }) {
  const { client } = household;
  return (
    <div className="feat-hub-grid">
      <div>
        {openCases && openCases.length > 0 ? (
          <div style={{ marginBottom: "var(--space-4)" }}>
            <OpenCasesCard cases={openCases} />
          </div>
        ) : null}
        {household.policies.length === 0 ? (
          <Card title="Policies">
            <EmptyState
              icon={<BookIcon size={32} />}
              title="No policies yet"
              message="When this household binds, their policies and your splits show here."
            />
          </Card>
        ) : (
          <>
            <div className="feat-policy-cards">
              {household.policies.map((p) => (
                <D2PolicyRow key={p._id} policy={p} identity={identity} />
              ))}
            </div>
            <p className="feat-d2row__footnote">
              ≈ estimates from current premium — your commission statement is the source of truth.
            </p>
          </>
        )}
      </div>

      <div style={{ display: "grid", gap: "var(--space-4)" }}>
        <Card title="Contact">
          <div className="feat-kv">
            <div className="feat-kv__row">
              <span className="feat-kv__key">Email</span>
              <span className="feat-kv__value">{client.email || "—"}</span>
            </div>
            <div className="feat-kv__row">
              <span className="feat-kv__key">Phone</span>
              <span className="feat-kv__value">{client.telephone || "—"}</span>
            </div>
            <div className="feat-kv__row">
              <span className="feat-kv__key">Address</span>
              <span className="feat-kv__value">{client.address || "—"}</span>
            </div>
          </div>
        </Card>
        <OwnUploadsCard household={household} />
      </div>
    </div>
  );
}

export default ReferralOverview;
